import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ApiResponseEnvelope,
  PaginatedListResult,
  PaginatedQueryOptions,
} from '@doctor-scheduling/shared/models';
import { DayOfWeek } from '@doctor-scheduling/shared/constants';
import { API_BASE_URL } from './api-base-url.token';
import { buildListHttpParams } from './list-query.util';

/** Weekly availability window for a doctor. */
export interface AvailabilityListItem {
  id: string;
  doctorId: string;
  clinicId: string;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
  slotDuration: number;
  isActive: boolean;
  doctor?: {
    id: string;
    specialization: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  };
}

/** Payload for creating a doctor availability rule. */
export interface CreateAvailabilityPayload {
  doctorId: string;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
  slotDuration?: number;
  isActive?: boolean;
}

/** Partial payload for updating an availability rule. */
export interface UpdateAvailabilityPayload {
  dayOfWeek?: DayOfWeek;
  startTime?: string;
  endTime?: string;
  slotDuration?: number;
  isActive?: boolean;
}

/** HTTP client for doctor availability CRUD. */
@Injectable({
  providedIn: 'root',
})
export class AvailabilityApi {
  /** Angular HTTP client used for API requests. */
  private readonly http = inject(HttpClient);
  /** NestJS API origin from the web app environment. */
  private readonly apiBaseUrl = inject(API_BASE_URL);
  /** Base URL for availability routes under `/api/v1/availability`. */
  private readonly baseUrl = `${this.apiBaseUrl}/api/v1/availability`;

  /**
   * Lists availability rules with Eaze-style page/sort/filter query params.
   */
  findAll(
    opts?: PaginatedQueryOptions
  ): Observable<ApiResponseEnvelope<PaginatedListResult<AvailabilityListItem>>> {
    return this.http.get<ApiResponseEnvelope<PaginatedListResult<AvailabilityListItem>>>(
      this.baseUrl,
      { params: buildListHttpParams(opts) }
    );
  }

  findByDoctor(doctorId: string): Observable<ApiResponseEnvelope<AvailabilityListItem[]>> {
    return this.http.get<ApiResponseEnvelope<AvailabilityListItem[]>>(
      `${this.baseUrl}/doctor/${doctorId}`
    );
  }

  create(data: CreateAvailabilityPayload): Observable<ApiResponseEnvelope<AvailabilityListItem>> {
    return this.http.post<ApiResponseEnvelope<AvailabilityListItem>>(this.baseUrl, data);
  }

  update(
    id: string,
    data: UpdateAvailabilityPayload
  ): Observable<ApiResponseEnvelope<AvailabilityListItem>> {
    return this.http.put<ApiResponseEnvelope<AvailabilityListItem>>(`${this.baseUrl}/${id}`, data);
  }

  delete(id: string): Observable<ApiResponseEnvelope<{ success: boolean }>> {
    return this.http.delete<ApiResponseEnvelope<{ success: boolean }>>(`${this.baseUrl}/${id}`);
  }

  getCalendarBands(
    fromDate: string,
    toDate: string,
    doctorIds?: string
  ): Observable<
    ApiResponseEnvelope<
      Array<{
        doctorId: string;
        date: string;
        startTime: string;
        endTime: string;
      }>
    >
  > {
    let params = new HttpParams().set('fromDate', fromDate).set('toDate', toDate);
    if (doctorIds) params = params.set('doctorIds', doctorIds);
    return this.http.get<
      ApiResponseEnvelope<
        Array<{
          doctorId: string;
          date: string;
          startTime: string;
          endTime: string;
        }>
      >
    >(`${this.baseUrl}/calendar`, { params });
  }
}
